export type FeedbackExitReason = 'resolve' | 'remove';

type MutationStatus = 'pending' | 'succeeded' | 'failed';

type ExitRecord = {
  reason: FeedbackExitReason;
  relatedIds: string[];
  animationDone: boolean;
  mutationStatus: MutationStatus;
};

export type FeedbackExitCommit = {
  feedbackId: string;
  reason: FeedbackExitReason;
  relatedIds: string[];
};

/**
 * Two-phase exit: cache/refetch commit only after the mutation succeeds
 * and the row animation has finished (or the list has unmounted).
 */
export class FeedbackListExitLifecycle {
  private readonly records = new Map<string, ExitRecord>();
  
  private unmounted = false;

  begin(feedbackId: string, reason: FeedbackExitReason, relatedIds?: string[]) {
    this.records.set(feedbackId, {
      reason,
      relatedIds: relatedIds?.length ? [...new Set([feedbackId, ...relatedIds])] : [feedbackId],
      animationDone: false,
      mutationStatus: 'pending',
    });
  }

  confirm(feedbackId: string): FeedbackExitCommit | null {
    const record = this.records.get(feedbackId);
    if (!record || record.mutationStatus === 'failed') return null;
    record.mutationStatus = 'succeeded';
    return this.takeIfReady(feedbackId);
  }

  fail(feedbackId: string): boolean {
    const record = this.records.get(feedbackId);
    if (!record) return false;
    this.records.delete(feedbackId);
    return true;
  }

  animationComplete(feedbackId: string): FeedbackExitCommit | null {
    const record = this.records.get(feedbackId);
    if (!record) return null;
    record.animationDone = true;
    return this.takeIfReady(feedbackId);
  }

  /** Commit already-succeeded exits. Pending ones wait for confirm/fail. */
  unmount(): FeedbackExitCommit[] {
    this.unmounted = true;
    const commits: FeedbackExitCommit[] = [];
    for (const feedbackId of [...this.records.keys()]) {
      const commit = this.takeIfReady(feedbackId);
      if (commit) commits.push(commit);
    }
    return commits;
  }

  clear() {
    this.records.clear();
    this.unmounted = false;
  }

  has(feedbackId: string) {
    return this.records.has(feedbackId);
  }

  private takeIfReady(feedbackId: string): FeedbackExitCommit | null {
    const record = this.records.get(feedbackId);
    if (!record || record.mutationStatus !== 'succeeded') return null;
    if (!record.animationDone && !this.unmounted) return null;
    this.records.delete(feedbackId);
    return { feedbackId, reason: record.reason, relatedIds: record.relatedIds };
  }
}
